<template>
  <div>
    <div
      v-if="accessDenied"
      class="settings--limit-width" >
      <h1 class="settings__title">
        Organisation billing
      </h1>
      <Forbidden />
    </div>

    <div
      v-else
      class="settings--limit-width">
      <h1 class="settings__title">
        Organisation billing
      </h1>

      <Spinner
        v-if="!billingDetails || !history || !nextInvoice"
        class="mt-5"
        :size="20"
        :thickness="2" />

      <!-- managed by reseller -->
      <div v-else-if="billingDetails.pricing_plan.reseller">
        <p class="reseller-icon">🤝</p>
        <p class="reseller-title">Your account is managed by the AI Number reseller</p>
        <p class="reseller">Your account and fees are managed by the reseller</p>
        <a
          :href="billingDetails.pricing_plan.website"
          target="_blank"
          class="reseller" >{{ billingDetails.pricing_plan.name }}</a>
      </div>

      <!-- Standard view -->
      <template v-else>
        <div
          v-if="billingDetails && billingDetails.pricing_plan"
          class="info flex">
          <div class="greedy">
            <h1
              id="plan-title"
              style="display: inline-block; cursor: default;">{{ billingDetails && billingDetails.pricing_plan ? `${billingDetails.pricing_plan.name} plan` : '' }}</h1>
            <p v-if="currentOrganisation && currentOrganisation.activate_items && currentOrganisation.activate_items.is_trial">You don't have an active plan at the moment.</p>
            <p v-else>{{ billingDetails && billingDetails.pricing_plan ? `Your plan will renew on ${renewDateFormated} for estimated ${nextInvoice ? targetCurrency.symbol + nextInvoice.overall_amount : ''}` : '' }}</p>
          </div>
          <b-button
            class="flex center-all"
            @click="updatePlan">
            <SBIcon
              class="mr-2"
              :icon="SBIconList.REFRESH_DOUBLE"
              :color="SBColors.blackPrimary"
              :size="12"/>
            <span>Update plan</span>
          </b-button>
        </div>

        <!-- Plan popover -->
        <b-popover
          target="plan-title"
          triggers="hover"
          boundary-padding="40"
          placement="bottom"
          custom-class="plan-details-popover"
          :delay="0" >
          <h3>{{ billingDetails.pricing_plan.name }} plan</h3>
          <div
            class="grid">
            <template v-if="billingDetails.pricing_plan.included_items">
              <div>
                <h3 class="details-title">Included:</h3>
                <p
                  v-for="(item, index) in billingDetails.pricing_plan.included_items.included_in_plan"
                  :key="`included-${index}`"
                  class="details-item" >
                  <span
                    v-if="item.icon"
                    class="emoji-fix" >{{ item.icon }} </span>{{ item.quantity }} x {{ item.name }}
                </p>
              </div>
              <div class="strong">
                <!-- {{ Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 10, style: 'currency', currency: billingDetails.subscription.price.currency }).format(billingDetails.subscription.price.amount) }} / month -->
                {{ targetCurrency.symbol }}{{ billingDetails.subscription.price.amount }} / month
              </div>
            </template>
            <template v-if="billingDetails.pricing_plan.included_items">
              <!-- Usage fees -->
              <template v-for="(item, index) in billingDetails.pricing_plan.included_items.usage_fees">
                <div :key="`usage-title-${index}`"><h3 class="details-title">{{ item.name }}</h3></div>
                <div
                  :key="`usage-${index}`"
                  class="strong" >{{ targetCurrency.symbol }}{{ item.price.amount }} / {{ billingDetails.subscription.billing_cycle }}</div>
              </template>

              <!-- Overage -->
              <template v-for="(item, index) in billingDetails.pricing_plan.included_items.overage">
                <div :key="`overage-title-${index}`"><h3 class="details-title">{{ item.name }}</h3></div>
                <div
                  :key="`overage-${index}`"
                  class="strong" >{{ targetCurrency.symbol }}{{ item.price.amount }} / {{ billingDetails.subscription.billing_cycle }}</div>
              </template>
            </template>
          </div>
        </b-popover>

        <!-- INVOICE -->
        <div class="invoice">
          <!-- FREE TRIAL -->
          <template v-if="isFreeTrial">
            <h3 style="margin-top: 11px; margin-bottom: 20px; font-size: 14px; font-weight: 500;">When your free trial funds are spent, you'll automatically be signed up for the {{ billingDetails.pricing_plan.name }} plan at {{ targetCurrency.symbol }}{{ billingDetails.subscription.price.amount }} per month. To update your subscription, please reach out to <span class="underline">support@ainumber.com.</span></h3>
            <div
              class="plan-details dashed"
              style="margin-bottom: 13px;" >
              <div class="flex">
                <div
                  v-if="billingDetails.pricing_plan.included_items"
                  style="margin-right: 15px;">
                  <h3 class="details-title">Included:</h3>
                  <p
                    v-for="(item, index) in billingDetails.pricing_plan.included_items.included_in_plan"
                    :key="`included-${index}`" >
                    <span
                      v-if="item.icon"
                      class="emoji-fix" >{{ item.icon }} </span>{{ item.quantity }} x {{ item.name }}
                  </p>
                </div>
                <div
                  v-if="billingDetails.pricing_plan.included_items"
                  style="width: 60%;">
                  <h3 class="details-title">Overage & usage fees:</h3>
                  <p
                    v-for="(item, index) in billingDetails.pricing_plan.included_items.overage"
                    :key="`overage-${index}`" >
                    {{ item.name }} - {{ targetCurrency.symbol }}{{ item.price.amount }} / {{ billingDetails.subscription.billing_cycle }}
                  </p>
                  <p
                    v-for="(item, index) in billingDetails.pricing_plan.included_items.usage_fees"
                    :key="`usage-${index}`" >
                    {{ item.name }} - {{ targetCurrency.symbol }}{{ item.price.amount }} / {{ billingDetails.subscription.billing_cycle }}
                  </p>
                </div>
              </div>
            </div>
          </template>
          <!-- ACTIVE PLAN -->
          <template v-else>
            <div
              v-if="billingDetails"
              ref="tableGrid"
              class="table__grid">
              <span
                v-if="meteredItems.length"
                class="title first column-name sticky-top">{{ renewDateMinus1 | dateMMMDD }} - {{ renewDate | dateMMMDD }}</span>
              <span
                v-if="meteredItems.length"
                class="title first column-name sticky-top">Qty</span>
              <span
                v-if="meteredItems.length"
                class="title first column-name sticky-top">Unit</span>
              <span
                v-if="meteredItems.length"
                class="title first column-name sticky-top right">Total</span>

              <template
                v-for="(item, index) in meteredItems" >
                <!-- Name column cell -->
                <div
                  :key="`${index}-status-mi`"
                  :class="[`hover-${index}`, 'flex-item', 'flex-align-center', { 'border-bottom': index < (meteredItems.length - 1) }, 'pr-2']" >
                  <p
                    class="ellipsis"
                    :inner-html.prop="item.name" />
                </div>

                <!-- Quantity column cell -->
                <div
                  :key="`${index}-url-mi`"
                  :class="[`hover-${index} flex-item flex-align-center`, { 'border-bottom': index < (meteredItems.length - 1) }, 'pr-2']" >
                  <p class="ellipsis" >{{ Number(item.quantity).toLocaleString() }}</p>
                </div>

                <!-- Unit price column cell -->
                <div
                  :key="`${index}-role-mi`"
                  :class="[`hover-${index} flex-item flex-align-center`, { 'border-bottom': index < (meteredItems.length - 1) }, 'pr-2']" >
                  <p>{{ targetCurrency.symbol }}{{ item.unitPrice }}</p>
                </div>

                <!-- Price column cell -->
                <div
                  :key="`${index}-number-mi`"
                  :class="[`hover-${index} flex-item flex-align-center right`, { 'border-bottom': index < (meteredItems.length - 1) }]" >
                  <p>{{ targetCurrency.symbol }}{{ item.price }}</p>
                </div>
              </template>

              <span class="title column-name sticky-top">{{ renewDate | dateMMMDD }} - {{ renewDatePlus1 | dateMMMDD }}</span>
              <span class="title column-name sticky-top" >{{ meteredItems.length ? '' : 'Qty' }}</span>
              <span class="title column-name sticky-top" >{{ meteredItems.length ? '' : 'Unit' }}</span>
              <span class="title column-name sticky-top right" >{{ meteredItems.length ? '' : 'Total' }}</span>

              <template
                v-for="(item, index) in licensedItems" >
                <!-- Name column cell -->
                <div
                  :key="`${index}-status-li`"
                  :class="[`hover-${index}`, 'flex-item', 'flex-align-center', { 'border-bottom': index < (licensedItems.length - 1) }, 'pr-2']" >
                  <p
                    class="ellipsis"
                    :inner-html.prop="item.name" />
                </div>

                <!-- Quantity column cell -->
                <div
                  :key="`${index}-url-li`"
                  :class="[`hover-${index} flex-item flex-align-center`, { 'border-bottom': index < (licensedItems.length - 1) }, 'pr-2']" >
                  <p class="ellipsis" >{{ Number(item.quantity).toLocaleString() }}</p>
                </div>

                <!-- Unit price column cell -->
                <div
                  :key="`${index}-role-li`"
                  :class="[`hover-${index} flex-item flex-align-center`, { 'border-bottom': index < (licensedItems.length - 1) }, 'pr-2']" >
                  <p>{{ targetCurrency.symbol }}{{ item.unitPrice }}</p>
                </div>

                <!-- Total price column cell -->
                <div
                  :key="`${index}-number-li`"
                  :class="[`hover-${index} flex-item flex-align-center right`, { 'border-bottom': index < (licensedItems.length - 1) }]" >
                  <p>{{ targetCurrency.symbol }}{{ item.price }}</p>
                </div>
              </template>
            </div>
            <div class="separator" />
            <div class="flex center-all total">
              <p class="greedy">Total</p>
              <p class="price">{{ targetCurrency.symbol }}{{ nextInvoice.overall_amount }}</p>
            </div>
          </template>
        </div>

        <!-- BALANCE -->
        <template v-if="funds">
          <div class="header-title">
            <h3>Balance</h3>
            <p>Your account balance is used for usage-based activities, such as AI responses.</p>
          </div>
          <div
            class="invoice elevated p20"
            style="margin-top: 20px; margin-bottom: 25px;">
            <div class="funds">
              <div class="flex">
                <div class="flex column greedy">
                  <p class="mb-2">{{ isFreeTrial ? 'Free trial funds' : 'Available funds' }}</p>
                  <div class="flex align-baseline">
                    <h1 :class="{ red: funds.status === 'insufficient_funds', yellow: funds.status === 'low_funds' }">{{ ballance }}</h1>
                    <p>{{ funds.currency }}</p>
                  </div>
                </div>
                <b-button
                  v-if="!isFreeTrial"
                  class="flex center-all fit-height"
                  @click="showTopupModal()">
                  <span>Add funds</span>
                </b-button>
              </div>
              <p
                v-if="funds.status === 'low_funds'"
                style="margin-top: 5px; font-weight: 400;"
                class="yellow" >Your balance has fallen below the top-up threshold. Add funds to prevent service interruptions.</p>
              <p
                v-if="funds.status === 'insufficient_funds'"
                style="margin-top: 5px; font-weight: 400;"
                class="red" >If your balance falls below {{ maxAllowedNegativeBallance }}, all your AI assistants and usage-based features will stop working.</p>
            </div>
            <div
              class="separator"
              style="margin-top: 23px; margin-bottom: 20px;" />
            <div
              class="recharge"
              :class="{ dashed: isFreeTrial }">
              <template v-if="fixedMonthlyAmount">
                <h4>Fixed monthly auto recharge amount</h4>
                <PrefixInput
                  v-model="fixedMonthlyAmount"
                  disabled
                  :prefix="targetCurrency.symbol" />
                <div
                  class="separator"
                  style="margin-top: 18px; margin-bottom: 15px;" />
              </template>
              <div class="flex align-start">
                <div class="flex column greedy">
                  <div class="flex flex-align-center">
                    <h4>{{ autoRechargeTitle }}</h4>
                    <span
                      :class="[autoRechargeStatus ? 'enabled' : 'disabled']"
                      class="ml-2 mb-1 pill">{{ autoRechargeStatus ? 'Enabled' : 'Disabled' }}</span>
                  </div>
                  <p v-if="isFreeTrial">Automatically recharge your balance when your free trial funds are spent.</p>
                  <p v-else>Automatically recharge your balance when it falls below your set threshold.</p>
                </div>

                <SwitchCheckbox
                  v-model="autoRechargeStatus"
                  class="balance-toggle"
                  :height="23"
                  :aspect="1.65"
                  @input="autoRecharge($event)"/>
              </div>
              <div
                class="separator"
                style="margin-top: 18px; margin-bottom: 15px;" />
              <template v-if="isFreeTrial">
                <h4>Add this amount when free trial funds are spent</h4>
                <PrefixInput
                  v-model="topupAmount"
                  :prefix="targetCurrency.symbol" />
              </template>
              <template v-else>
                <ValidationObserver
                  ref="customFieldObserver"
                  v-slot="{ valid, dirty }" >
                  <h4>When available funds fall below</h4>
                  <ValidationProvider
                    v-slot="{ errors }"
                    :rules="{
                      number: {
                        message: `Invalid format. Enter a whole-dollar amount in ${targetCurrency.code} (e.g., ${targetCurrency.symbol}123).`,
                      },
                      minNumber: {
                        min: 1,
                        message: `Amount must be at least ${targetCurrency.symbol}1.`,
                      },
                      maxNumber: {
                        max: 5000,
                        message: `Amount must be lower than ${targetCurrency.symbol}5,000.`,
                      },
                      noDecimals: {
                        message: `Invalid format. Enter a whole-dollar amount in ${targetCurrency.code} (e.g., ${targetCurrency.symbol}123).`,
                      },
                      required: true,
                    }"
                    name="Password" >
                    <PrefixInput
                      v-model="thresholdAmount"
                      :prefix="targetCurrency.symbol" />
                    <div
                      v-if="errors[0]"
                      class="validation-error flex flex-align-center">
                      <SBIcon
                        class="mr-2"
                        :icon="SBIconList.ERROR"
                        :color="SBColors.redPrimary"
                        :size="12" />
                      <span>{{ errors[0] }}</span>
                    </div>
                  </ValidationProvider>
                  <h4 style="margin-top: 15px;">Add this amount</h4>
                  <ValidationProvider
                    v-slot="{ errors }"
                    :rules="{
                      number: {
                        message: `Invalid format. Enter a whole-dollar amount in ${targetCurrency.code} (e.g., ${targetCurrency.symbol}123).`,
                      },
                      minNumber: {
                        min: 1,
                        message: `Amount must be at least ${targetCurrency.symbol}1.`,
                      },
                      maxNumber: {
                        max: 5000,
                        message: `Amount must be lower than ${targetCurrency.symbol}5,000.`,
                      },
                      noDecimals: {
                        message: `Invalid format. Enter a whole-dollar amount in ${targetCurrency.code} (e.g., ${targetCurrency.symbol}123).`,
                      },
                      required: true,
                    }"
                    name="Password2" >
                    <PrefixInput
                      v-model="topupAmount"
                      :prefix="targetCurrency.symbol" />
                    <div
                      v-if="errors[0]"
                      class="validation-error flex flex-align-center">
                      <SBIcon
                        class="mr-2"
                        :icon="SBIconList.ERROR"
                        :color="SBColors.redPrimary"
                        :size="12" />
                      <span>{{ errors[0] }}</span>
                    </div>
                  </ValidationProvider>
                  <div
                    class="separator"
                    style="margin-top: 18px; margin-bottom: 18px;" />
                  <div class="flex">
                    <span class="greedy" />
                    <b-button
                      v-if="dirty && !saving && topupChangePending"
                      :disabled="saving"
                      class="flex"
                      @click="resetTopup">
                      <span>Cancel</span>
                    </b-button>
                    <b-button
                      variant="primary"
                      :disabled="!dirty || !valid || !topupChangePending"
                      class="flex settings__actions--save"
                      :class="{loading: saving}"
                      style="margin-left: 10px;"
                      @click="updateTopUpSettings">
                      <span>Save</span>
                      <Spinner
                        v-if="saving"
                        :size="14"
                        :thickness="2"
                        :color="SBColors.white" />
                    </b-button>
                  </div>
                </ValidationObserver>
              </template>
            </div>
          </div>
        </template>

        <!-- USAGE -->
        <template v-if="logs">
          <div class="header-title">
            <h3>Usage</h3>
            <p>Track your account usage for the current subscription period ({{ logs.date_from | dateMMMMDo }} - {{ logs.date_to | dateMMMMDo }}).</p>
          </div>
          <div
            class="elevated logs-container" >
            <h3
              v-if="!logs.logs.length"
              class="no-data">You don't have any usage activity yet.</h3>
            <div
              v-else>
              <div
                v-for="(log, index) in logs.logs"
                :key="index" >
                <div class="flex title">
                  <p class="greedy">{{ log.name }}</p>
                  <p>Total</p>
                </div>
                <div
                  v-for="(l, i) in log.logs"
                  :key="i"
                  class="flex log-item">
                  <p class="greedy">{{ l.name }}</p>
                  <p>{{ l.count }}</p>
                </div>
              </div>
            </div>
          </div>
        </template>
        <!-- PAYMENT METHOD -->
        <div class="header-title">
          <h3>Payment method</h3>
          <p>Manage your payment methods, billing details, and invoices.</p>
        </div>
        <div class="row">
          <!-- Payment and Billing details -->
          <div class="billing-box-wrapper col-xl-6">
            <div class="billing-box">
              <h3>Payment & billing details</h3>
              <Spinner
                v-if="!billingDetails && !billingDetailsError"
                :size="18"
                :thickness="2" />
              <template v-else-if="billingDetailsError">
                <span class="emoji big center">⚠️</span>
                <p class="error-message" >There is an error retriving your billing details.<br />Please try again later.</p>
              </template>
              <template v-else>
                <div
                  class="flex flex-align-center mb-10px"
                  :class="{ expired: cardExpired }" >
                  <div
                    v-if="billingDetails.payment_method.method === 'manual payment'"
                    class="bank-icon" />
                  <div
                    v-else
                    class="credit-card"
                    :class="[billingDetails.payment_method.brand, { unset: !billingDetails.payment_method.brand}]" />
                  <div />

                  <p
                    v-if="billingDetails.payment_method.brand"
                    v-b-popover.hover="{ content: 'Card expired', animation: false, customClass: 'basic-popover', placement: 'top', disabled: !cardExpired }" >{{ billingDetails.payment_method.brand }} •••• •••• •••• {{ billingDetails.payment_method.last_4 }}</p>
                  <p v-else-if="billingDetails.payment_method.method === 'manual payment'">Bank transfer payments</p>
                  <p
                    v-else
                    class="unset">No payment method</p>
                </div>
                <span
                  v-if="billingDetails.payment_method.brand"
                  class="button"
                  @click="editPaymentDetails" >{{ billingDetails.payment_method.brand || billingDetails.payment_method.method ? 'Edit ' : 'Add ' }}payment method</span>
                <a
                  v-else-if="billingDetails.payment_method.method === 'manual payment'"
                  href="mailto:support@ainumber.com?subject=Payment method" >
                  <span class="button">Contact support to edit payment method</span>
                </a>

                <!-- Company details -->
                <div class="company-details">
                  <span
                    v-for="(detail, index) in billingDetailsFiltered"
                    :key="index" >
                    <h3>{{ detail.title }}</h3>
                    <p v-if="detail.title === 'Country'">{{ detail.value | countryName }}</p>
                    <p v-else>{{ detail.value }}</p>
                  </span>
                </div>
                <span
                  class="button"
                  @click="editBillingDetails" >Edit billing details</span>
              </template>
            </div>
          </div>

          <!-- Payment history -->
          <div class="billing-box-wrapper col-xl-6">
            <div class="billing-box">
              <h3>Payment History</h3>
              <Spinner
                v-if="history === null"
                :size="18"
                :thickness="2" />
              <p
                v-else-if="!history.length"
                class="no-invoices" >Your invoices will appear here.</p>
              <template v-else>
                <div
                  v-if="payBy"
                  class="overdue flex flex-align-center">
                  <SBIcon
                    class="mr-3"
                    :icon="SBIconList.TRIANGLE_EXCLAMATION"
                    :color="SBColors.$blackPrimary"
                    :size="12"/>
                  <p>Pay open invoices by {{ payBy | dateMMMMDD(true) }} to avoid service interruptions.</p>
                </div>
                <div
                  v-for="(historyInvoice, index) in historyProccessed.slice(0, 12)"
                  :key="index"
                  class="history flex center-all"
                  :class="[historyInvoice.status]" >
                  <SBIcon
                    v-if="historyInvoice.refunded"
                    v-b-popover.hover="{ content: 'Refunded', animation: false, customClass: 'basic-popover', placement: 'top' }"
                    class="mr-2"
                    :icon="SBIconList.ARROW_ROUNDED"
                    color="#0e8854"
                    background-color="#DCF5E3"
                    :hover-size="18"
                    :border-radius="50"
                    :size="10"/>
                  <SBIcon
                    v-else-if="historyInvoice.partiallyRefunded"
                    v-b-popover.hover="{ content: `Partially refunded - ${targetCurrency.symbol}${historyInvoice.refund_amount}`, animation: false, customClass: 'basic-popover', placement: 'top' }"
                    class="mr-2"
                    :icon="SBIconList.ARROW_DOTS"
                    color="#0e8854"
                    background-color="#DCF5E3"
                    :hover-size="18"
                    :border-radius="50"
                    :size="10"/>
                  <SBIcon
                    v-else-if="historyInvoice.status === 'open'"
                    v-b-popover.hover="{ content: 'Open', animation: false, customClass: 'basic-popover', placement: 'top' }"
                    class="mr-2"
                    :icon="SBIconList.ARROW_ROUNDED"
                    color="#697FFF"
                    background-color="#e0e4ff"
                    :hover-size="18"
                    :border-radius="50"
                    :rotate="135"
                    :size="10"/>
                  <SBIcon
                    v-else-if="historyInvoice.status === 'paid'"
                    v-b-popover.hover="{ content: 'Paid', animation: false, customClass: 'basic-popover', placement: 'top' }"
                    class="mr-2"
                    :icon="SBIconList.DONE_TICK"
                    color="#54626c"
                    background-color="#e6ebed"
                    :hover-size="18"
                    :border-radius="50"
                    :size="8"/>
                  <SBIcon
                    v-else-if="historyInvoice.status === 'failed'"
                    v-b-popover.hover="{ content: 'Overdue', animation: false, customClass: 'basic-popover', placement: 'top' }"
                    class="mr-2"
                    :icon="SBIconList.CLOSE_THICK"
                    color="#ec5a61"
                    background-color="#ffd8d9"
                    :hover-size="18"
                    :border-radius="50"
                    :size="7"/>
                  <p>{{ (historyInvoice.created_at * 1000) | dateMMMDDYYYY }} - {{ targetCurrency.symbol }}{{ historyInvoice.total }}</p>
                  <span class="greedy dashes" />

                  <a
                    v-if="historyInvoice.status === 'open'"
                    :href="historyInvoice.hosted_invoice_url"
                    target="_blank" >
                    <b-button class="pay-btn flex center-all" >
                      <span>Pay</span>
                    </b-button>
                  </a>
                  <a
                    v-else
                    :href="historyInvoice.hosted_invoice_url"
                    target="_blank" >View</a>
                </div>

                <span
                  v-if="historyProccessed.length > 12"
                  class="all-invoices button"
                  @click="showAllInvoices" >View all invoices</span>
              </template>
            </div>
          </div>
        </div>
      </template>
    </div>

    <EditPaymentMethodModal
      ref="editPaymentMethodModalRef"
      @new-card-last-four="fetchBillingDetails" />
    <EditBillingInformationModal
      ref="editBillingInformationModalRef"
      @reload-billing-details="fetchBillingDetails" />
    <PaymentHistoryModal
      ref="paymentHistoryModalRef" />
    <TopUpModal
      ref="topupModalRef"
      :currency="funds && funds.currency ? funds.currency : 'USD'"
      @status="topupStatus" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import api from '@/helpers/api';
import Forbidden from '@/components/errors/Forbidden';
import Spinner from '@/components/common/Spinner';
import SBColors from '@/helpers/lists/colors';
import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';
import EditPaymentMethodModal from '@/components/business/settings/modals/EditPaymentMethod.modal';
import EditBillingInformationModal from '@/components/business/settings/modals/EditBillingInformation.modal';
import SwitchCheckbox from '@/components/common/SwitchCheckbox.v2';
import UserPermissionsMixin from '@/mixins/UserPermissions.mixin';
import PrefixInput from '@/components/common/PrefixInput';
import ToasterMixin from '@/mixins/Toaster';
import Currencies from '@/helpers/lists/currencies';
import { ModalSize } from '@/helpers/enums/modal.enum';
import PaymentHistoryModal from './modals/PaymentHistory.modal';
import TopUpModal from './modals/TopUp.modal';

export default {
  name: 'BillingSettings',
  components: {
    Spinner,
    SBIcon,
    Forbidden,
    EditPaymentMethodModal,
    EditBillingInformationModal,
    PaymentHistoryModal,
    SwitchCheckbox,
    PrefixInput,
    TopUpModal,
  },
  mixins: [
    UserPermissionsMixin,
    ToasterMixin,
  ],
  data() {
    return {
      forbidden: false,
      billingDetails: null,
      billingDetailsError: null,
      history: null,
      nextInvoice: null,
      autoRechargeStatus: false,
      thresholdAmount: '11',
      topupAmount: '11',
      thresholdAmountBackup: '11',
      topupAmountBackup: '11',
      saving: false,
      funds: null,
      logs: null,
      SBColors,
      SBIconList,
      Currencies,
    };
  },
  computed: {
    ...mapGetters({
      currentOrganisation: 'organisation/currentOrganisation',
      updateBillingPage: 'organisation/updateBillingPage',
    }),
    accessDenied() {
      return this.forbidden || !this.checkPermission('manage_organisation_settings');
    },
    fixedMonthlyAmount() {
      return this.funds && this.funds.included_funds ? this.funds.included_funds : 0;
    },
    isFreeTrial() {
      return this.billingDetails && this.billingDetails.subscription && this.billingDetails.subscription.is_trial;
    },
    autoRechargeTitle() {
      return this.isFreeTrial ? 'Auto-recharge when your free trial funds are spent' : 'Low balance auto recharge';
    },
    autoRechargeDescription() {
      return this.isFreeTrial ? 'Automatically recharge your balance when your free trial funds are spent.' : 'Automatically recharge your balance when it falls below your set threshold.';
    },
    ballance() {
      return this.funds && this.funds.amount ? this.funds.amount : 0;
    },
    maxAllowedNegativeBallance() {
      return this.funds && this.funds.negative_threshold ? this.funds.negative_threshold : 0;
    },
    targetCurrency() {
      const targetCurrency = Currencies.find(currency => currency.code === this.billingDetails.subscription.price.currency);
      if (targetCurrency) return targetCurrency;
      return Currencies.find(currency => currency.code === 'USD');
    },
    topupChangePending() {
      return this.thresholdAmount !== this.thresholdAmountBackup || this.topupAmount !== this.topupAmountBackup;
    },
    billingDetailsFiltered() {
      const itemsInFilteredDetails = [
        { title: 'Company name', path: 'billing_data.billing.name' },
        { title: 'Country', path: 'billing_data.billing.address.country_code' },
        { title: 'Person', path: 'billing_data.invoice.custom_fields:Person' },
        { title: 'Billing email', path: 'billing_data.billing.email' },
        { title: 'Address', path: 'billing_data.billing.address.line1' },
        { title: 'City', path: 'billing_data.billing.address.city' },
        { title: 'State', path: 'billing_data.billing.address.state' },
        { title: 'Postal code', path: 'billing_data.billing.address.zip_code' },
        { title: 'Tax ID', path: 'billing_data.billing.tax_id' },
      ];
      const billingDetailsFiltered = [];
      itemsInFilteredDetails.forEach((item) => {
        let value;
        if (item.path.startsWith('billing_data.invoice.custom_fields')) {
          const customField = item.path.split(':')[1];
          const customFields = this.billingDetails && this.billingDetails.billing_data && this.billingDetails.billing_data.invoice && this.billingDetails.billing_data.invoice.custom_fields ? this.billingDetails.billing_data.invoice.custom_fields : [];
          const targetItem = customFields.find(item => item.name === customField);
          if (targetItem) {
            value = targetItem.value;
          }
        } else {
          value = this.$lodash.get(this.billingDetails, item.path);
        }
        if (value) {
          billingDetailsFiltered.push({
            title: item.title,
            value,
          });
        }
      });

      return billingDetailsFiltered;
    },
    cardExpired() {
      let expired = false;
      if (this.billingDetails.payment_method && this.billingDetails.payment_method.expire_month) {
        const cardExpireDate = this.$moment().year(this.billingDetails.payment_method.expire_year).month(this.billingDetails.payment_method.expire_month - 1).startOf('month');
        expired = this.$moment().isAfter(cardExpireDate);
      }
      return expired;
    },
    historyProccessed() {
      const historyProccessed = [];
      this.history.forEach((history) => {
        const historyClone = JSON.parse(JSON.stringify(history));
        if (history.status === 'paid' && history.refund_amount && (history.refund_amount === history.total)) {
          historyClone.refunded = true;
        } else if (history.status === 'paid' && history.refund_amount && (history.refund_amount !== history.total)) {
          historyClone.partiallyRefunded = true;
        }
        historyProccessed.push(historyClone);
      });

      return historyProccessed;
    },
    payBy() {
      if (this.billingDetails && this.billingDetails.expiration_date) {
        return this.billingDetails.expiration_date;
      }
      return null;
    },
    renewDate() {
      if (!this.billingDetails.subscription || !this.billingDetails.subscription.next_invoice_date) {
        return '-';
      }

      const now = this.$moment();
      const billingDate = this.$moment(false, false).unix(this.billingDetails.subscription.next_invoice_date);
      const billingDateOfMonth = billingDate.date();
      const currentMonth = now.month();
      const currentYear = now.year();
      const constructedBillingDate = this.$moment([currentYear, currentMonth, billingDateOfMonth]);
      const nextDate = this.$moment().isSameOrAfter(constructedBillingDate) ? constructedBillingDate.add(1, 'month') : constructedBillingDate;

      return nextDate;
    },
    renewDatePlus1() {
      if (this.renewDate !== '-') {
        return this.renewDate.clone().add(1, 'month');
      }

      return this.renewDate;
    },
    renewDateMinus1() {
      if (this.renewDate !== '-') {
        return this.renewDate.clone().subtract(1, 'month');
      }

      return this.renewDate;
    },
    renewDateFormated() {
      if (this.renewDate !== '-') {
        return this.$options.filters.dateDoMMMMYYYY(this.renewDate);
      }

      return this.renewDate;
    },
    licensedItems() {
      const licensedItems = [];

      if (!this.nextInvoice || !this.nextInvoice.licenced_items) {
        return null;
      }

      this.nextInvoice.licenced_items.forEach((item) => {
        if (!item.proration) { // prorated items go to metered items
          licensedItems.push({
            name: item.description,
            quantity: item.quantity,
            unitPrice: item.unit_amount,
            price: item.unit_amount * item.quantity,
          });
        }
      });

      return licensedItems;
    },
    meteredItems() {
      const meteredItems = [];
      if (this.nextInvoice && this.nextInvoice.metered_items) {
        // const businessesIds = Object.keys(this.nextInvoice.metered_items.WhatsApp);
        // businessesIds.forEach((businessId) => {
        this.nextInvoice.metered_items.forEach((mi) => {
          meteredItems.push({
            name: mi.description,
            quantity: mi.quantity,
            unitPrice: mi.unit_price,
            price: mi.unit_price * mi.quantity,
          });
          // const numberData = this.nextInvoice.metered_items.WhatsApp[businessId];
          // Business-Initiated Conversation
          // numberData['Business-Initiated Conversation'].forEach((item) => {
          //   meteredItems.push({
          //     name: item.description,
          //     quantity: item.quantity,
          //     unitPrice: item.unit_price,
          //     price: item.unit_price * item.quantity,
          //   });
          // });
          // User-Initiated Conversation
          // numberData['User-Initiated Conversation'].forEach((item) => {
          //   meteredItems.push({
          //     name: item.description,
          //     quantity: item.quantity,
          //     unitPrice: item.unit_price,
          //     price: item.unit_price * item.quantity,
          //   });
          // });
          // Free entry point - do not show in billing
          // numberData['Free entry point']['Business-Initiated Conversation'].forEach((item) => {
          //   meteredItems.push({
          //     name: item.description,
          //     quantity: item.quantity,
          //     unitPrice: item.unit_price,
          //     price: item.unit_price * item.quantity,
          //   });
          // });
          // numberData['Free entry point']['User-Initiated Conversation'].forEach((item) => {
          //   meteredItems.push({
          //     name: item.description,
          //     quantity: item.quantity,
          //     unitPrice: item.unit_price,
          //     price: item.unit_price * item.quantity,
          //   });
          // });
          // Free tier - do not show in billing
          // numberData['Free tier']['Business-Initiated Conversation'].forEach((item) => {
          //   meteredItems.push({
          //     name: item.description,
          //     quantity: item.quantity,
          //     unitPrice: item.unit_price,
          //     price: item.unit_price * item.quantity,
          //   });
          // });
          // numberData['Free tier']['User-Initiated Conversation'].forEach((item) => {
          //   meteredItems.push({
          //     name: item.description,
          //     quantity: item.quantity,
          //     unitPrice: item.unit_price,
          //     price: item.unit_price * item.quantity,
          //   });
          // });
        // });
        });
      }

      if (this.nextInvoice && this.nextInvoice.licenced_items) {
        this.nextInvoice.licenced_items.forEach((item) => {
          if (item.proration) { // prorated items go to metered items
            meteredItems.push({
              name: item.description,
              quantity: item.quantity,
              unitPrice: item.unit_amount,
              price: item.unit_amount * item.quantity,
            });
          }
        });
      }

      // meteredItems.push({
      //   name: 'Remaining time on 2 × Members after 21 Jul 2022',
      //   quantity: 2,
      //   price: 10.35,
      // });
      // meteredItems.push({
      //   name: 'Unused time on Members after 21 Jul 2022',
      //   quantity: 1,
      //   price: -5.18,
      // });
      // meteredItems.push({
      //   name: 'Remaining time on 3 × Members after 21 Jul 2022',
      //   quantity: 3,
      //   price: 15.53,
      // });
      // meteredItems.push({
      //   name: 'Unused time on 2 × Members after 21 Jul 2022',
      //   quantity: 2,
      //   price: -10.35,
      // });
      // meteredItems.push({
      //   name: 'Remaining time on 4 × Members after 21 Jul 2022',
      //   quantity: 4,
      //   price: 20.70,
      // });
      // meteredItems.push({
      //   name: 'Unused time on 3 × Members after 21 Jul 2022',
      //   quantity: 3,
      //   price: -15.53,
      // });
      // meteredItems.push({
      //   name: 'Remaining time on 5 × Members after 21 Jul 2022',
      //   quantity: 5,
      //   price: 25.87,
      // });
      // meteredItems.push({
      //   name: 'Unused time on 4 × Members after 21 Jul 2022',
      //   quantity: 4,
      //   price: -20.70,
      // });

      return meteredItems;
    },
  },
  watch: {
    accessDenied() {
      if (!this.accessDenied) {
        this.fetchBillingDetails();
        this.fetchNextInvoice();
        this.fetchHistory();
      }
    },
    // silently update billing data
    updateBillingPage() {
      this.fetchBillingDetails();
      this.fetchNextInvoice();
      this.fetchHistory();
    },
    funds() {
      this.thresholdAmount = this.funds.automatic_topup_threshold.toString();
      this.topupAmount = this.funds.topup_amount.toString();
      this.thresholdAmountBackup = this.funds.automatic_topup_threshold.toString();
      this.topupAmountBackup = this.funds.topup_amount.toString();
      this.autoRechargeStatus = this.funds.automatic_topup_enabled;
    },
  },
  mounted() {
    window.eventBus.$on('403', () => { this.forbidden = true; });
    this.finalizeSplashView();
    if (!this.accessDenied && this.currentOrganisation.id) {
      this.fetchFunds();
      this.fetchBillingDetails();
      this.fetchNextInvoice();
      this.fetchHistory();
      this.fetchLogs();
    }
  },
  methods: {
    ...mapActions({
      finalizeSplashView: 'organisation/finalizeSplashView',
      // fetchBillingDetails: 'organisation/fetchBillingDetails',
    }),
    autoRecharge(enabled) {
      this.autoRechargeStatus = enabled;

      api('PUT', `/organisation/${this.currentOrganisation.id}/spending/automatic-topup`, { enabled }, null, 'w')
        .then((response) => {
          if (response.status === 200) {
            if (enabled) {
              this.showToaster('Auto recharge has been successfully enabled.', null, null, 'OK');
            } else {
              this.showToaster('Auto recharge has been successfully disabled.', null, null, 'OK');
            }
          }
        });
    },
    updateTopUpSettings() {
      const topUpAmountNumber = Number(this.topupAmount);
      const thresholdAmountNumber = Number(this.thresholdAmount);
      api('PUT', `/organisation/${this.currentOrganisation.id}/spending/settings`, { automatic_topup_threshold: thresholdAmountNumber, topup_amount: topUpAmountNumber }, null, 'w')
        .then((response) => {
          if (response.status === 200) {
            this.topupAmountBackup = this.topupAmount;
            this.thresholdAmountBackup = this.thresholdAmount;
            this.showToaster('Auto recharge preferences have been successfully updated.', null, null, 'OK');
            this.fetchFunds();
          }
        });
    },
    editBillingDetails() {
      this.$refs.editBillingInformationModalRef.show(this.billingDetailsFiltered);
    },
    editPaymentDetails() {
      this.$refs.editPaymentMethodModalRef.show();
    },
    fetchFunds() {
      api('GET', `/organisation/${this.currentOrganisation.id}/spending/funds`, null, null, 'w')
        .then((response) => {
          if (response.status === 200) {
            this.funds = response.data;
          }
        });
    },
    fetchLogs() {
      api('GET', `/organisation/${this.currentOrganisation.id}/spending/logs`, null, null, 'w')
        .then((response) => {
          if (response.status === 200) {
            this.logs = response.data;
          }
        });
    },
    fetchBillingDetails() {
      api('GET', `/organisation/${this.currentOrganisation.id}/billing/details`, null, null, 'w')
        .then((response) => {
          if (response.status === 200) {
            // response.data.payment_method.expire_year = 2020;
            this.billingDetails = response.data;
          }
        })
        .catch((error) => {
          this.billingDetailsError = error;
        });
    },
    fetchNextInvoice() {
      api('GET', `/organisation/${this.currentOrganisation.id}/billing/invoice/next`, null, null, 'w')
        .then((response) => {
          if (response.status === 200) {
            this.nextInvoice = response.data;
          }
        });
    },
    fetchHistory() {
      api('GET', `/organisation/${this.currentOrganisation.id}/billing/invoice/history`, null, null, 'w')
        .then((response) => {
          if (response.status === 200) {
            this.history = [...response.data];
          }
        });
    },
    showAllInvoices() {
      this.$refs.paymentHistoryModalRef.show(this.historyProccessed);
    },
    resetTopup() {
      this.topupAmount = this.topupAmountBackup;
      this.thresholdAmount = this.thresholdAmountBackup;
    },
    showTopupModal() {
      this.$refs.topupModalRef.show();
    },
    topupStatus(status) {
      if (status) {
        this.fetchFunds();
        this.fetchHistory();
        this.fetchLogs();
        this.showToaster('Funds have been successfully added.', null, null, 'OK');
      } else {
        this.showToaster('Funds have not been added.', null, null, 'error');
      }
    },
    updatePlan() {
      this.$numberInfoModal.show({
        size: ModalSize.MEDIUM,
        title: 'Contact support to update your billing',
        message: 'To request any changes to your plan, please contact our support team at support@ainumber.com.',
        cancelButtonText: 'Close',
      });
    },
  },
};
</script>

<style lang="scss" scoped>
// TODO - this should be a component
.overdue {
  margin: 15px 0;
  padding: 11px 15px;
  border-radius: 5px;
  border: solid 1px #f8e9e9;
  background-color: #fff7dd;
}

.mb-10px {
  margin-bottom: 10px;
}

.emoji.big {
  font-size: 20px;
}

.center {
  display: block;
  text-align: center;
}

.error-message {

}

.pay-btn {
  padding: 3px 11px 1px !important;
  span {
    font-size: 12px;
    font-weight: 500;
  }
}

.reseller-icon {
  font-size: 53px;
  margin-top: 38px;
}
.reseller-title {
  margin: 7px 0 7px 0;
  font-size: 18px;
  font-weight: 500;
  color: #697fff;
}

.reseller {
  margin: 7px 0 3px 0;
  font-size: 14px !important;
  color: $blackUltralight;
}

a.reseller:hover {
  color: $blackPrimary;
}

.header-title {
  h3 {
    font-size: 20px;
    font-weight: 500;
    line-height: normal;
    color: $blackPrimary;
    margin-bottom: 6px;
  }
  p {
    font-size: 14px;
    font-weight: 500;
    line-height: 1.37;
    color: $blackUltralight;
    margin-bottom: 20px;
  }
}

.info {
  align-items: flex-end;
  margin: 0 0 20px 0;

  h1 {
    margin: 35px 0 8px 0;
    font-size: 20px;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    color: $bluePrimary;
  }

  p {
    margin: 0 0 0 0;
    font-size: 14px;
    font-weight: 500;
    font-stretch: normal;
    font-style: normal;
    color: $blackUltralight;
  }
}

.table__grid {
  grid-template-columns: minmax(50px, auto) minmax(50px, auto) auto min-content !important;
  column-gap: 0px;
  row-gap: 0px;

  .right {
    text-align: right;
  }

  .full-width {
    grid-column: 1 / 5 !important;
  }

  .column-name {
    border: none !important;
  }
}
.invoice {
  margin: 20px 0;
  padding: 9px 20px;
  border-radius: 5px;
  box-shadow: 0 1px 3px 0 rgba(48, 54, 70, 0.1);
  border: solid 1px $greyBorderSecondary;
  background-color: $white;

  .title, .item {
    padding: 9px 0 9px 0;
    margin-bottom: -9px;
    margin-top: 10px;

    &.first {
      margin-top: 0;
    }
  }

  .flex-item {
    padding: 12px 0 12px 0;

    &.border-bottom {
      border-bottom: 1px dashed #dde6e9 !important;
    }
  }

  .title, p {
    font-size: 14px;
    font-weight: 500;
    font-stretch: normal;
    font-style: normal;
    letter-spacing: normal;
    color: $blackPrimary;
  }

  .title, .title p, p.multiplyer {
    color: $blackUltralight !important;
  }

  .separator {
    border: solid 1px $greyBorderSecondary;
    margin: 9px 0;
  }

  .total {
    padding: 9px 0;

    .price {
      font-size: 18px;
    }
  }
}

.row {
  margin: 0 !important;
}

.recharge {
  h4 {
    font-size: 14px;
    font-weight: 500;
    color: $blackPrimary;
    margin-bottom: 4px;
  }

  p {
    font-size: 14px;
    font-weight: normal;
    color: $blackUltralight;
  }

  .pill {
    font-size: 10px;
    font-weight: 500;
    color: $white;
    background-color: $greenPrimary;
    border-radius: 5px;
    padding: 3px 5px;
    height: fit-content;

    &.disabled {
      background-color: $greyLight;
    }
  }
}

.billing-box-wrapper {
  padding: 0 !important;
  * {
    font-size: 14px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
  }

  .billing-box {
    margin: 0 10px 20px 0;
    padding: 19px 20px;
    border-radius: 5px;
    border: solid 1px $greyBorderSecondary;
    background-color: $greyUltralight;

    .no-invoices {
      font-size: 14px;
      font-weight: 500;
      line-height: 1.37;
      color: $greyPrimary;
      margin-top: -9px;
    }

    h3 {
      margin: 0 0 20px 0;
      font-size: 14px;
      font-weight: 500;
      font-stretch: normal;
      font-style: normal;
      text-align: left;
      color: $blackUltralight;
    }

    a, span.button {
      font-weight: 500;
      color: $bluePrimary;
      text-decoration: none;
      cursor: pointer;

      &:hover {
        color: $blackPrimary;
      }
    }

    .company-details {
      margin: 20px 0;
      h3 {
        margin-bottom: 4px;
        font-weight: normal;
        color: $blackUltralight;
      }

      p {
        margin-bottom: 10px;
      }
    }

    .bank-icon {
      mask-image: url('~@/assets/img/SB-Icons/bank-icon.svg');
      mask-repeat: no-repeat;
      mask-position: center;
      background-color: $blackLight;
      background-repeat: no-repeat;
      margin-right: 8px;
      height: 14px;
      width: 16px;
    }

    .credit-card {
      mask-image: url('~@/assets/img/SB-Icons/credit-card.svg');
      mask-repeat: no-repeat;
      mask-position: center;
      background-color: $blackLight;
      background-repeat: no-repeat;
      margin-right: 8px;
      height: 16px;
      width: 18px;

      &.unset {
        background-color: $greyPrimary;
      }

      &.Visa {
        mask-image: unset;
        background-color: unset;
        background-image: url('~@/assets/img/credit-cards/visa.png');
        background-size: contain;
        background-position: center center;
      }
    }

    .expired {
      color: $redPrimary;

      .credit-card {
        // background-color: $redPrimary;
      }
    }

    .history {
      margin-bottom: 15px;

      &:last-of-type {
        margin-bottom: 0;
      }

      span.dashes {
        margin: 10px 10px 0;
        border-bottom: 1px dashed #dde6e9;
      }
      a {
        color: $blackUltralight;
        font-weight: normal;
        &:hover {
          color: $bluePrimary;
        }
      }

      &.open {
        color: #697fff !important;
      }
    }

    .all-invoices.button {
      margin-top: 20px;
      display: inline-block;
    }
  }

  &:last-of-type .billing-box {
    margin: 0 0 20px 10px;
  }
}

.unset {
  color: $greyPrimary;
}

.funds {
  align-items: baseline;
  h1 {
    font-size: 38px;
    font-weight: 500;
    line-height: 1;
    color: $blackPrimary;
    margin-right: 5px;
  }
  p {
    font-size: 14px;
    font-weight: 500;
    line-height: 1;
    color: $blackUltralight;
  }
}

.plan-details-popover {
  max-width: 490px;

  h3 {
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 13px;
    line-height: 1.2;
    color: $bluePrimary;
  }

  .details-title {
    font-size: 14px;
    font-weight: 500;
    line-height: normal !important;
    margin-bottom: 0 !important;
    margin-top: 0 !important;
    color: $blackPrimary;
  }

  .details-item {
    font-size: 14px;
    font-weight: 400;
    line-height: normal !important;
    margin-bottom: 2px !important;
    margin-top: 0 !important;
    color: $blackUltralight !important;

    .emoji-fix {
      font-size: 12px !important;
      top: -2px;
      position: relative;
    }
  }

  .grid {
    display: grid;
    grid-template-columns: minmax(300px, 2fr) minmax(0, 1fr);
    // gap: 10px 0;

    & > * {
      border-top: 1px dashed $greyBorderModal;
      padding: 14px 14px 14px 0;

      &:nth-child(2n) {
        border-left: 1px dashed $greyBorderModal;
        padding: 14px 0 14px 14px;
      }

      &:last-child {
        border-bottom: 1px dashed $greyBorderModal;
      }

      &:nth-last-child(2) {
        border-bottom: 1px dashed $greyBorderModal;
      }
    }
  }
}

// .details-title {
//   font-size: 14px;
//   font-weight: 500;
//   margin-bottom: 10px;
// }

.plan-details {
  padding: 14px 0 0;

  h3 {
    font-size: 14px;
    font-weight: 500;
    line-height: 1.37;
    color: $blackPrimary;
    margin-bottom: 4px;
  }

  p {
    font-size: 14px;
    line-height: 1.4;
    font-weight: 400;
    color: $blackLight;
  }

  .emoji-fix {
    font-size: 12px !important;
    top: -2px;
    position: relative;
  }
}

.logs-container {
  margin-top: 20px !important;
  margin-bottom: 25px !important;
  padding: 20px !important;

  .title {
    margin-bottom: 12px;

    * {
      font-size: 14px;
      font-weight: 500;
      line-height: 1.37;
      color: $blackUltralight;
    }
  }

  .log-item {
    border-bottom: 1px dashed #dde6e9;
    padding-bottom: 12px;
    margin-bottom: 12px;

    * {
      font-size: 14px;
      font-weight: 500;
      line-height: 1.37;
      color: $blackPrimary;
    }

    &:last-of-type {
      margin-bottom: 0;
    }
  }
}

::v-deep .balance-toggle {
  label {
    margin: 0 !important;
  }
}

@media (max-width: $large) {
  .billing-box {
    margin-left: 0px !important;
    margin-right: 0px !important;
  }
}
</style>
